/**
 * Sessao do jogador no navegador: token, usuario logado e persistencia.
 *
 * CONTRATO (lote D e dono; lotes C e E so LEEM):
 *   const session = new Session(ctx)
 *   await session.init()          restaura do localStorage e revalida em /api/auth/me
 *   session.user                  { id, email, name, nickname } | null
 *   session.token                 string | null
 *   session.isLoggedIn            boolean
 *   session.set(user, token)      grava, persiste e emite AUTH_CHANGED
 *   session.clear()               desloga, limpa e emite AUTH_CHANGED
 *   session.dispose()
 *
 * Emite ctx.events EVENTS.AUTH_CHANGED { user } em toda troca - quem depende de
 * login (Lobby, NetSync, Menu) escuta esse evento em vez de consultar em loop.
 *
 * O token fica em localStorage. Nao e o mais seguro que existe (cookie httpOnly
 * seria), mas o WebSocket precisa mandar o token no `hello` a partir do JS, e
 * cookie httpOnly nao e legivel pelo JS - seria preciso um segundo canal so para
 * isso. Fica registrado como consciente, nao como esquecido.
 */

import { Api, ApiError } from './Api.js';

const STORAGE_KEY = 'blackout.session';

export class Session {
  constructor(ctx) {
    this.ctx = ctx;
    this.user = null;
    this.token = null;
    this._api = new Api(ctx);
  }

  get api() {
    return this._api;
  }

  get isLoggedIn() {
    return !!(this.user && this.token);
  }

  async init() {
    const restored = this._readStored();
    if (!restored) return;

    this.user = restored.user;
    this.token = restored.token;
    this._api.setToken(this.token);

    try {
      const user = await this._api.me();
      if (!user) {
        this.clear();
        return;
      }
      this.set(user, this.token);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        this.clear();
        return;
      }

      // Uma indisponibilidade temporaria nao deve expulsar o jogador da conta.
      this._emitChanged();
      console.warn('[Session] nao foi possivel revalidar a sessao; mantendo os dados locais:', err);
    }
  }

  set(user, token) {
    if (!user || typeof token !== 'string' || !token) {
      throw new TypeError('Usuario e token validos sao obrigatorios.');
    }

    this.user = user;
    this.token = token;
    this._api.setToken(token);
    this._writeStored();
    this._emitChanged();
  }

  clear() {
    this.user = null;
    this.token = null;
    this._api.setToken(null);

    try {
      globalThis.localStorage?.removeItem?.(STORAGE_KEY);
    } catch (err) {
      console.warn('[Session] nao foi possivel remover a sessao local:', err);
    }

    this._emitChanged();
  }

  _readStored() {
    try {
      const raw = globalThis.localStorage?.getItem?.(STORAGE_KEY);
      if (!raw) return null;

      const value = JSON.parse(raw);
      if (typeof value?.token !== 'string' || !value.token || !value?.user || typeof value.user !== 'object') {
        globalThis.localStorage?.removeItem?.(STORAGE_KEY);
        return null;
      }

      return { token: value.token, user: value.user };
    } catch (err) {
      console.warn('[Session] sessao local invalida ou indisponivel:', err);
      try {
        globalThis.localStorage?.removeItem?.(STORAGE_KEY);
      } catch (removeErr) {
        console.warn('[Session] nao foi possivel limpar a sessao local invalida:', removeErr);
      }
      return null;
    }
  }

  _writeStored() {
    try {
      globalThis.localStorage?.setItem?.(STORAGE_KEY, JSON.stringify({
        token: this.token,
        user: this.user
      }));
    } catch (err) {
      console.warn('[Session] nao foi possivel persistir a sessao local:', err);
    }
  }

  _emitChanged() {
    const eventName = this.ctx?.EVENTS?.AUTH_CHANGED;
    if (eventName) this.ctx?.events?.emit?.(eventName, { user: this.user });
  }

  dispose() {
    this._api?.setToken?.(null);
    this.ctx = null;
  }
}
